import { Badge } from "@risalabs_frontend_org/oasis-ui-kit";
import { memo, useCallback, useEffect, useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import { CrossIcon } from "../../../svg/cross-icon";
import { CrossWhiteBg } from "../../../svg/crossWhiteBg";
import NewSideModal from "../sideModal/newSideModal";

export interface FacilityWithId {
  id: string;
  name: string;
}

interface UserAccessSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UserAccessData) => void;
  memberName: string;
  memberEmail: string;
  memberId: string;
  currentRole: string;
  internalOrgIds: string[];
  externalOrgIds: string[];
  availableInternalOrgs: FacilityWithId[];
  availableExternalOrgs: FacilityWithId[];
  isSaving?: boolean;
}

export interface UserAccessData {
  memberId: string;
  name: string;
  email: string;
  role: string;
  internalOrgIds: string[];
  externalOrgIds: string[];
  internalOrgs: string[];
  externalOrgs: string[];
}

const UserAccessSidePanel = memo((props: UserAccessSidePanelProps) => {
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedInternalOrgIds, setSelectedInternalOrgIds] = useState<
    string[]
  >([]);
  const [selectedExternalOrgIds, setSelectedExternalOrgIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (props.isOpen) {
      setSelectedRole(props.currentRole);
      setSelectedInternalOrgIds(props.internalOrgIds);
      setSelectedExternalOrgIds(props.externalOrgIds);
    }
  }, [
    props.isOpen,
    props.currentRole,
    props.internalOrgIds,
    props.externalOrgIds,
  ]);

  const handleClose = useCallback(() => {
    props.onClose();
  }, [props.onClose]);

  const getInternalOrgNames = useCallback(() => {
    return selectedInternalOrgIds
      .map(
        (id) => props.availableInternalOrgs.find((org) => org.id === id)?.name,
      )
      .filter(Boolean) as string[];
  }, [selectedInternalOrgIds, props.availableInternalOrgs]);

  const getExternalOrgNames = useCallback(() => {
    return selectedExternalOrgIds
      .map(
        (id) => props.availableExternalOrgs.find((org) => org.id === id)?.name,
      )
      .filter(Boolean) as string[];
  }, [selectedExternalOrgIds, props.availableExternalOrgs]);

  const handleSave = useCallback(() => {
    props.onSave({
      memberId: props.memberId,
      name: props.memberName,
      email: props.memberEmail,
      role: selectedRole,
      internalOrgIds: selectedInternalOrgIds,
      externalOrgIds: selectedExternalOrgIds,
      internalOrgs: getInternalOrgNames(),
      externalOrgs: getExternalOrgNames(),
    });
  }, [
    props.onSave,
    props.memberId,
    selectedRole,
    selectedInternalOrgIds,
    selectedExternalOrgIds,
    getInternalOrgNames,
    getExternalOrgNames,
  ]);

  const toggleInternalOrg = useCallback((orgId: string) => {
    setSelectedInternalOrgIds((prev) =>
      prev.includes(orgId)
        ? prev.filter((id) => id !== orgId)
        : [...prev, orgId],
    );
  }, []);

  const toggleExternalOrg = useCallback((orgId: string) => {
    setSelectedExternalOrgIds((prev) =>
      prev.includes(orgId)
        ? prev.filter((id) => id !== orgId)
        : [...prev, orgId],
    );
  }, []);

  return (
    <NewSideModal
      isOpen={props.isOpen}
      onClose={handleClose}
      id="user-access-side-panel"
    >
      <div className="flex h-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-5">
          <h2 className="m-0 font-['Lato'] text-lg font-bold leading-[1.625rem]">
            {props.memberName}&apos;s User Access
          </h2>
          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent p-1 hover:opacity-70"
            onClick={handleClose}
          >
            <CrossIcon fillColor="#374151" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-7">
            <h3 className="m-0 mb-3.5 font-['Lato'] text-sm font-semibold leading-[1.375rem] text-[#374151]">
              Select the Internal Orgs
            </h3>
            <div className="flex flex-wrap gap-2.5">
              {props.availableInternalOrgs.map((org) => {
                const isSelected = selectedInternalOrgIds.includes(org.id);
                return (
                  <div
                    key={org.id}
                    className="cursor-pointer transition-opacity duration-150 hover:opacity-85"
                    onClick={() => toggleInternalOrg(org.id)}
                  >
                    <Badge
                      text={org.name}
                      backgroundColor={isSelected ? "#1a7f64" : "transparent"}
                      textColor={isSelected ? "#ffffff" : "#374151"}
                      borderColor={isSelected ? "#1a7f64" : "#D1D5DB"}
                      borderWidth="1px"
                      size="medium"
                      rightIcon={
                        isSelected ? (
                          <CrossWhiteBg width="12" height="12" />
                        ) : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-7">
            <h3 className="m-0 mb-3.5 text-sm font-semibold leading-[1.375rem] text-[#374151]">
              Select the External Orgs
            </h3>
            <div className="flex flex-wrap gap-2.5">
              {props.availableExternalOrgs.map((org) => {
                const isSelected = selectedExternalOrgIds.includes(org.id);
                return (
                  <div
                    key={org.id}
                    className="cursor-pointer transition-opacity duration-150 hover:opacity-85"
                    onClick={() => toggleExternalOrg(org.id)}
                  >
                    <Badge
                      text={org.name}
                      backgroundColor={isSelected ? "#1a7f64" : "transparent"}
                      textColor={isSelected ? "#ffffff" : "#374151"}
                      borderColor={isSelected ? "#1a7f64" : "#D1D5DB"}
                      borderWidth="1px"
                      size="medium"
                      rightIcon={
                        isSelected ? (
                          <CrossWhiteBg width="12" height="12" />
                        ) : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="user-access-panel__footer mt-auto flex justify-center gap-3 border-t border-[#e5e7eb] bg-white px-6 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
            disabled={props.isSaving ?? false}
          >
            Cancel
          </Button>
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={props.isSaving ?? false}
          >
            {props.isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </NewSideModal>
  );
});

UserAccessSidePanel.displayName = "UserAccessSidePanel";

export default UserAccessSidePanel;
